import React from "react";
import { useDispatch } from "react-redux";
import Icon from "../../../../components/image/Icon";
import { icNextBlue } from "../../../../components/image/image-src";
import { ProdAttrPicker } from "../../../../components/prodattr-picker/ProdAttrPicker";
import { ProductPopup } from "../../../../components/product-detail/ProductPopup";
import { RichTextBox } from "../../../../components/richtext/RichTextBox";
import { useAbleAdd2Cart } from "../../../../hooks/useAbleAdd2Cart";
import { useBookingState } from "../../../../hooks/useBookingState";
import { useNaviProduct } from "../../../../hooks/useNaviProduct";
import { bookingActions } from "../../../../redux/slices/bookingSlice";
import { useSelectProductModal } from "./useSelectProductModal";
import { useSearchParamsOfBooking } from "../../../../hooks/searchParams/useSearchParamsOfBooking";
import { useTranslation } from "react-i18next";
import { useTranslationEx } from "../../../../hooks/useTranslationEx";

export interface SelectProductModalProps {
  id: number;
  slug: string;
  isOpen?: boolean;
  onSubmit?: () => void;
  closeModal?: () => void;
}

const SelectProductModal: React.FC<SelectProductModalProps> = (props) => {
  const { t } = useTranslation();
  const bookingState = useBookingState({
    mode: "POPUP",
  });
  const jj = useSelectProductModal(props);
  const product = jj.product;
  const { isUpgrade } = useSearchParamsOfBooking();
  const canDeselect = (function () {
    if (isUpgrade) {
      // *BOOKMARK* NEWLY PICKED PRODUCT
      return (
        bookingState.childrenLs?.find((x) => x?.product?.id === product?.id)
          ?.canEdit === undefined
      );
    }
    return true;
  })();

  const dispatch = useDispatch();

  const ableAdd2Cart = useAbleAdd2Cart({
    totalPrice: undefined, // not care
    bookingAttributes: bookingState.bookingAttributes,
    mode: "POPUP",
  });
  const closeModal = () => {
    jj.bookingDispatch.bookingPickLs([]);
    jj.closeModal?.();
  };
  const onDeselect = () => {
    if (!canDeselect) return;
    dispatch(
      bookingActions.deSelectChildProducts({
        productIds: [jj.id],
      }),
    );
    closeModal?.();
  };
  const onApply = () => {
    dispatch(bookingActions.selectChildProduct(bookingState));
    closeModal?.();
  };

  return (
    <>
      {jj.isOpen && !!product && (
        <ProductPopup product={product} closeModal={closeModal}>
          {/* Body */}
          <>
            <ProductModalIntroText product={product} showViewProduct={true} />
            <ProdAttrPicker d={product} mode="POPUP" />
          </>
          {/* Footer */}
          <>
            <div className="mt-auto flex gap-3 pt-4">
              <button
                type="button"
                onClick={onDeselect}
                className="w-full rounded-[3.4375rem] border border-[#232323] py-[0.8125rem] text-base font-semibold leading-[1.25rem] text-[#232323] hover:bg-gray-100 disabled:pointer-events-none"
                disabled={!canDeselect}
              >
                {t("Deselect")}
              </button>
              <button
                type="button"
                disabled={!ableAdd2Cart}
                onClick={onApply}
                className={`${
                  ableAdd2Cart
                    ? "bg-primary text-[#232323]"
                    : "bg-[#DCDCDC] text-white"
                } popup-apply--btn w-full rounded-[3.4375rem] py-[0.8125rem] text-base font-semibold leading-[1.25rem] text-[#232323]`}
              >
                {t("Apply")}
              </button>
            </div>
          </>
        </ProductPopup>
      )}
    </>
  );
};

export const ProductModalIntroText = ({
  product,
  showViewProduct,
}: {
  product?: ProductDetailResponse;
  showViewProduct?: boolean;
}) => {
  const { naviProduct } = useNaviProduct();
  const { t } = useTranslation();
  const { t2 } = useTranslationEx();

  return (
    <div className="mb-10">
      <span className="rounded-[0.3125rem] bg-[#F7F8FA] px-2 py-[0.5625rem] text-[0.75rem] font-normal leading-[1.125rem] text-[#5E6D77]">
        {t2(product?.categoryName)}
      </span>
      <div className="relative flex justify-between gap-2 py-2">
        <h2 className="text-base font-semibold text-[#232323]">
          {t2(product?.name)}
        </h2>
        {showViewProduct && (
          <button
            className="ml-auto flex h-[2rem] shrink-0 basis-auto items-center justify-center gap-2 rounded-[99px] border border-[#007AFF] px-2.5 py-0 text-xs leading-[1.375rem] text-[#007AFF] sm:h-[2.5rem] sm:px-[2rem] sm:text-sm"
            type="button"
            onClick={() =>
              naviProduct({
                ...product,
                newWindow: true,
              })
            }
          >
            {t("View Product")} <Icon src={icNextBlue} />
          </button>
        )}
      </div>
      <RichTextBox
        className="max-h-[7.5rem] overflow-hidden overflow-y-auto !text-sm"
        richtext={product?.passPreview?.trim()}
        // noImage={true}
      />
    </div>
  );
};

export default SelectProductModal;
